import React, { useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { PageLoading } from "@/shared/ui/pageLoading";

const AUTH_NEXT_KEY = "jelloshot_auth_next";

function consumeStoredNextPath(): string | null {
  try {
    const next = sessionStorage.getItem(AUTH_NEXT_KEY);
    if (!next || !next.startsWith("/") || next.startsWith("//")) {
      return null;
    }
    sessionStorage.removeItem(AUTH_NEXT_KEY);
    return next;
  } catch {
    return null;
  }
}

interface PrivateRouteProps {
  children: React.ReactNode;
}

export const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const researchCompleted = useAuthStore(
    (state) => state.user?.research_completed,
  );
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading || !isAuthenticated || researchCompleted === false) {
      return;
    }
    const storedNext = consumeStoredNextPath();
    if (storedNext && storedNext !== `${location.pathname}${location.search}`) {
      navigate(storedNext, { replace: true });
    }
  }, [
    isAuthenticated,
    isLoading,
    researchCompleted,
    location.pathname,
    location.search,
    navigate,
  ]);

  if (isLoading) {
    return <PageLoading />;
  }

  if (!isAuthenticated) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  if (researchCompleted === false) {
    return <Navigate to="/welcome" replace />;
  }

  return <>{children}</>;
};
